import { useTranslator } from "@eo-locale/react";

import NextLink from "next/link";
import { MdOutlineEmail } from "react-icons/md";

import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  HTMLWrapper,
  Text,
  VStack
} from "@/storybook-components";

import useProjectPageTheme from "@/hooks/useProjectPageTheme";

import Alerts from "../alerts";
import type { AuthRoutes } from "./logic";

interface RegisteredLoginGateProps extends AuthRoutes {
  disableSignup?: boolean;
}

export const RegisteredLoginGate = ({
  login,
  register,
  target,
  disableSignup
}: RegisteredLoginGateProps): JSX.Element => {
  const { translate } = useTranslator();

  return (
    <Alerts
      status="info"
      title={translate("quickPoll.createAccountForPoll")}
      description={
        disableSignup ? (
          <HTMLWrapper
            html={translate("common.pleaseSignIn", {
              loginRoute: login,
              target
            })}
          />
        ) : (
          <HTMLWrapper
            html={translate("common.signInOrRegister", {
              loginRoute: login,
              registerRoute: register,
              target
            })}
          />
        )
      }
    />
  );
};

interface GuestLoginGateProps extends Pick<AuthRoutes, "login" | "target"> {
  onContinueWithEmail: () => void;
}

export const GuestLoginGate = ({
  login,
  target,
  onContinueWithEmail
}: GuestLoginGateProps): JSX.Element => {
  const { translate } = useTranslator();
  const { buttonSx } = useProjectPageTheme();

  return (
    <Center py={8} data-testid="quick-poll-guest-login-gate">
      <VStack
        gap={6}
        maxW="450px"
        width="100%"
        align="center"
        textAlign="center"
      >
        <VStack gap={3}>
          <Heading as="h2" size="md">
            {translate("quickPoll.verifyYourEmailToTakeThisPoll")}
          </Heading>
          <Text color="gray.600">
            {translate("quickPoll.weWillSendYouATimeCode")}
          </Text>
        </VStack>
        <VStack align="stretch" gap={3}>
          <Button
            variant="primary"
            leftIcon={<MdOutlineEmail />}
            css={buttonSx}
            onClick={onContinueWithEmail}
          >
            {translate("quickPoll.continueWithEmail")}
          </Button>
          <HStack justify="center">
            <Text>{translate("quickPoll.alreadyHaveAnAccount")}</Text>
            <NextLink href={login} target={target}>
              <Button variant="link">{translate("quickPoll.signIn")}</Button>
            </NextLink>
          </HStack>
        </VStack>
      </VStack>
    </Center>
  );
};

export const TakenMessage = ({
  thanksMsg
}: {
  thanksMsg?: string | null;
}): JSX.Element => {
  const { translate } = useTranslator();
  const customThanksMsg = thanksMsg?.trim();

  if (customThanksMsg) {
    return (
      <Box data-testid="quick-poll-thanks-message">
        <HTMLWrapper html={customThanksMsg} />
      </Box>
    );
  }

  return (
    <Alerts
      status="success"
      title={translate("quickPoll.thanksForParticipating")}
      data-testid="quick-poll-thanks-message"
    />
  );
};
